import { createApiUrl, API_ENDPOINTS } from '../config/api';
import { getAuthToken, setUserInfo, clearAllAuthData, getRefreshToken, persistSessionTokens } from './persistentStorage';
import logger from './logger';

interface TokenRefreshResponse {
  token: string;
  refreshToken?: string;
}

const resolveUserTypeFromToken = (token: string, fallback: 'user' | 'admin' = 'user'): 'user' | 'admin' => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.userType === 'admin' || payload.role === 'ADMIN') {
      return 'admin';
    }
    return 'user';
  } catch {
    return fallback;
  }
};

class TokenManager {
  private refreshPromise: Promise<boolean> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private readonly REFRESH_THRESHOLD = 2 * 60 * 1000; // Refresh when fewer than 2 minutes remain
  private readonly CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes
  private activityListenersAttached: boolean = false;

  constructor() {
    this.startTokenCheck();
    this.setupActivityListeners();
  }

  /**
   * Start periodic token validation and refresh
   */
  public startTokenCheck() {
    if (this.refreshTimer) {
      return;
    }

    this.checkAndRefreshToken();
    this.refreshTimer = setInterval(() => {
      this.checkAndRefreshToken();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Stop token checking (cleanup)
   */
  public stopTokenCheck() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.removeActivityListeners();
  }

  /**
   * Setup activity listeners to refresh token when user becomes active
   */
  private setupActivityListeners() {
    if (this.activityListenersAttached) {
      return; // Already attached
    }

    // Handle visibility change (tab switch, window minimize, etc.)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // User returned to the app - check and refresh token if needed
        logger.info('Token Manager: User became active, checking token status');
        await this.checkAndRefreshTokenOnActivity();
      }
    };

    // Handle window focus (user clicked back into the window)
    const handleFocus = async () => {
      logger.info('Token Manager: Window focused, checking token status');
      await this.checkAndRefreshTokenOnActivity();
    };

    // Attach listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    this.activityListenersAttached = true;

    // Store handlers for cleanup
    (this as any)._handleVisibilityChange = handleVisibilityChange;
    (this as any)._handleFocus = handleFocus;
  }

  /**
   * Remove activity listeners (cleanup)
   */
  private removeActivityListeners() {
    if (!this.activityListenersAttached) {
      return;
    }

    const handleVisibilityChange = (this as any)._handleVisibilityChange;
    const handleFocus = (this as any)._handleFocus;

    if (handleVisibilityChange) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    if (handleFocus) {
      window.removeEventListener('focus', handleFocus);
    }

    this.activityListenersAttached = false;
    (this as any)._handleVisibilityChange = null;
    (this as any)._handleFocus = null;
  }

  /**
   * Check and refresh token when user becomes active after being idle
   */
  private async checkAndRefreshTokenOnActivity(): Promise<boolean> {
    try {
      const userToken = await getAuthToken('user');
      const adminToken = await getAuthToken('admin');
      const token = userToken || adminToken;
      const userType = adminToken ? 'admin' : 'user';

      if (!token) {
        return false;
      }

      // If token is expired or near expiry, refresh it
      if (this.isTokenExpired(token) || this.isTokenNearExpiry(token)) {
        logger.info('Token Manager: Token expired or near expiry after idle period, refreshing...');
        return await this.refreshToken(userType);
      }

      return true;
    } catch (error) {
      logger.error('Token Manager: Error checking token on activity', error);
      return false;
    }
  }

  /**
   * Ensure a valid auth token is available (refreshing if near expiry).
   * Use before opening SSE or other token-sensitive connections.
   */
  public async ensureFreshToken(): Promise<string | null> {
    try {
      const adminToken = await getAuthToken('admin');
      const userToken = await getAuthToken('user');
      const userType = adminToken ? 'admin' : 'user';
      let token = adminToken || userToken;

      if (!token) {
        return null;
      }

      if (this.isTokenExpired(token) || this.isTokenNearExpiry(token)) {
        const refreshed = await this.refreshToken(userType);
        if (!refreshed) {
          return null;
        }
        token = (await getAuthToken(userType)) || null;
      }

      return token;
    } catch (error) {
      logger.error('Token Manager: ensureFreshToken failed', error);
      return null;
    }
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  private async checkAndRefreshToken(): Promise<boolean> {
    try {
      const userToken = await getAuthToken('user');
      const adminToken = await getAuthToken('admin');
      const token = userToken || adminToken;
      const userType = adminToken ? 'admin' : 'user';

      if (!token) {
        return false;
      }

      // Check if token is close to expiry
      if (this.isTokenNearExpiry(token)) {
        logger.info('Token approaching expiry, attempting refresh...');
        return await this.refreshToken(userType);
      }

      return true;
    } catch (error) {
      logger.error('Error checking token:', error);
      return false;
    }
  }

  /**
   * Check if token is near expiry or already expired
   */
  private isTokenNearExpiry(token: string): boolean {
    try {
      // Decode JWT token to check expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      
      // Check if token is expired or near expiry
      return (expiry - now) < this.REFRESH_THRESHOLD;
    } catch (error) {
      logger.error('Error parsing token expiry:', error);
      return true; // If we can't parse, assume it needs refresh
    }
  }

  /**
   * Check if token is already expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000;
      const now = Date.now();
      
      return now >= expiry;
    } catch (error) {
      logger.error('Error parsing token expiry:', error);
      return true; // If we can't parse, assume it's expired
    }
  }

  /**
   * Refresh authentication token
   */
  public async refreshToken(userType: 'user' | 'admin' = 'user'): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh(userType);
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(userType: 'user' | 'admin'): Promise<boolean> {
    try {
      const storedRefreshToken = await getRefreshToken();
      const currentAccessToken = await getAuthToken(userType);

      logger.info('Attempting token refresh...');
      
      const response = await fetch(createApiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(storedRefreshToken ? { refreshToken: storedRefreshToken } : {})
      });
      
      logger.info(`Token refresh response status: ${response.status}`);

      if (!response.ok) {
        if (response.status === 401) {
          if (currentAccessToken && !this.isTokenExpired(currentAccessToken)) {
            logger.warn('Token refresh failed, but access token is still valid - keeping session');
            return true;
          }
          logger.warn('Token refresh failed - clearing auth data');
          await clearAllAuthData();
          return false;
        }
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data: TokenRefreshResponse = await response.json();
      if (!data.token) {
        logger.error('Token refresh response did not include an access token');
        return false;
      }

      await persistSessionTokens(data.token, resolveUserTypeFromToken(data.token, userType), data.refreshToken);
      
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
          detail: { token: data.token, userType } 
        }));
        logger.info('Token refresh event dispatched');
      }
      
      logger.info('Token refreshed successfully');
      return true;

    } catch (error) {
      logger.error('Token refresh error:', error);

      if (error instanceof TypeError && error.message.includes('fetch')) {
        logger.warn('Token refresh failed due to network - preserving existing token');
        return true;
      }

      const currentAccessToken = await getAuthToken(userType);
      if (currentAccessToken && !this.isTokenExpired(currentAccessToken)) {
        logger.warn('Token refresh failed, but access token is still valid - keeping session');
        return true;
      }

      return false;
    }
  }

  /**
   * Force token refresh (can be called manually)
   */
  public async forceRefresh(userType: 'user' | 'admin' = 'user'): Promise<boolean> {
    logger.info('Forcing token refresh...');
    return this.refreshToken(userType);
  }

  /**
   * Handle app startup - check and refresh tokens if needed
   */
  public async handleAppStartup(): Promise<boolean> {
    try {
      logger.info('Token Manager: Handling app startup token validation');
      
      const userToken = await getAuthToken('user');
      const adminToken = await getAuthToken('admin');
      const storedRefreshToken = await getRefreshToken();
      const token = userToken || adminToken;
      const userType = adminToken ? 'admin' : 'user';

      if (!token && !storedRefreshToken) {
        logger.info('Token Manager: No token found on app startup');
        return false;
      }

      if (!token || this.isTokenExpired(token) || this.isTokenNearExpiry(token)) {
        logger.info('Token Manager: Access token missing, expired, or near expiry on app startup, attempting refresh');
        return await this.refreshToken(userType);
      }

      logger.info('Token Manager: Token is valid on app startup');
      return true;
    } catch (error) {
      logger.error('Token Manager: Error during app startup validation', error);
      return false;
    }
  }

  /**
   * Validate current token by checking with server
   */
  public async validateToken(): Promise<boolean> {
    try {
      const userToken = await getAuthToken('user');
      const adminToken = await getAuthToken('admin');
      const token = userToken || adminToken;
      const userType = adminToken ? 'admin' : 'user';

      if (!token) {
        return false;
      }

      const response = await fetch(createApiUrl(API_ENDPOINTS.AUTH_ME), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Request': userType === 'admin' ? 'true' : 'false'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        // Update stored user info with latest data
        await setUserInfo(userData, userType);
        return true;
      } else {
        // Token is invalid
        await clearAllAuthData();
        return false;
      }
    } catch (error) {
      logger.error('Token validation error:', error);
      
      // Don't clear tokens on network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return true;
      }
      
      return false;
    }
  }

  /**
   * Get token expiry information
   */
  public async getTokenInfo(): Promise<{
    hasToken: boolean;
    userType: 'user' | 'admin' | null;
    expiresAt: number | null;
    isNearExpiry: boolean;
  }> {
    const userToken = await getAuthToken('user');
    const adminToken = await getAuthToken('admin');
    const token = userToken || adminToken;
    const userType = adminToken ? 'admin' : userToken ? 'user' : null;

    if (!token) {
      return {
        hasToken: false,
        userType: null,
        expiresAt: null,
        isNearExpiry: false
      };
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const isNearExpiry = this.isTokenNearExpiry(token);

      return {
        hasToken: true,
        userType,
        expiresAt,
        isNearExpiry
      };
    } catch (error) {
      logger.error('Error parsing token info:', error);
      return {
        hasToken: true,
        userType,
        expiresAt: null,
        isNearExpiry: true
      };
    }
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

export default tokenManager;

// Export helper functions
export const refreshAuthToken = (userType: 'user' | 'admin' = 'user') => 
  tokenManager.refreshToken(userType);

export const validateAuthToken = () => 
  tokenManager.validateToken();

export const getTokenInfo = () => 
  tokenManager.getTokenInfo();

export const forceTokenRefresh = (userType: 'user' | 'admin' = 'user') => 
  tokenManager.forceRefresh(userType);

export const ensureFreshAuthToken = () => tokenManager.ensureFreshToken();

export const handleAppStartup = () => 
  tokenManager.handleAppStartup();
